import {
  ReactNode, useLayoutEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import { TextInput } from './TextInput';
import { useCursor } from '../../../util/useCursor';

export type DateInlineInputProps = {
  id: string;
  label?: ReactNode;
  labelStyles?: string;
  placeholder?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  onBlur: (v: string) => void;
  onFocus?: () => void;
  disabled?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  touchOverride?: boolean;
  styles?: string;
  visible?: boolean;
  small?: boolean;
  xs?: boolean;
  isTooltipError?: boolean;
  errors?: string[];
  warnings?: string[];
}

export function DateInlineInput({
  id,
  label,
  labelStyles,
  placeholder,
  required,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  prefix,
  suffix,
  touchOverride,
  styles,
  visible = true,
  small = false,
  xs = false,
  isTooltipError = false,
  errors = [],
  warnings = [],
}: DateInlineInputProps) {
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [changeCount, setChangeCount] = useState(0);
  const [recordCursor, restoreCursor] = useCursor(inputRef.current, focused);
  const dateFormat = 'DD/MM/YYYY';

  const formatValue = (v: string) => {
    const strippedValue = v.replaceAll('/', '');

    if (strippedValue.length === 2) {
      return `${strippedValue}/`;
    }
    if (strippedValue.length >= 4) {
      return `${strippedValue.slice(0, 2)}/${strippedValue.slice(2, 4)}/${strippedValue.slice(4, Math.min(8, strippedValue.length))}`;
    }
    return v;
  };

  const [formattedValue, setFormattedValue] = useState<string>(value ? moment(value).format(dateFormat) : '');

  const handleChange = (v: string) => {
    recordCursor();
    setChangeCount((prev) => prev + 1);
    if (v.length > formattedValue.length) {
      setFormattedValue(formatValue(v));
    } else {
      setFormattedValue(v);
    }

    onChange(v && moment(v, dateFormat).format('YYYY-MM-DDTHH:mm:ss[Z]'));
  };

  const handleFocus = () => {
    setFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setFocused(false);
    setFormattedValue((prev) => {
      const date = moment(prev, dateFormat);
      return date.isValid() ? date.format(dateFormat) : prev;
    });

    onBlur(formattedValue && moment(formattedValue, dateFormat).format('YYYY-MM-DDTHH:mm:ss[Z]'));
  };

  useLayoutEffect(() => {
    // Prevent cursor jumping
    if (formattedValue.replaceAll('/', '').length > 4) {
      restoreCursor();
    }
  }, [value, changeCount]);

  return (
    <TextInput
      ref={inputRef}
      id={id}
      label={label}
      labelStyles={labelStyles}
      placeholder={placeholder}
      value={formattedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      mutateValue={(v) => v.replaceAll(/[^\d/]/g, '').replaceAll(/\/+/g, '/')}
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      touchOverride={touchOverride}
      required={required}
      styles={styles}
      visible={visible}
      small={small}
      xs={xs}
      isTooltipError={isTooltipError}
      errors={errors}
      warnings={warnings}
    />
  );
}
